/**
 * This component renders the dropdown menu for the courses menu. This menu
 * includes both the main menu and the submenu. The main menu is the first level
 * of the dropdown. If the conditions are met, the first item(s) in the main menu
 * will be pinned links. The pinned links are the "Browse By Standards" link and
 * the "Explore Khanmigo" link. One, both, or neither of these links may be rendered
 * depending on the conditions, but the standards guides link will always come
 * before the "Explore Khanmigo" link. The pinned links are followed by the
 * curation pages.
 *
 * The submenu is the second level - it is a single list of courses under a
 * shared curation page.
 *
 * Submenu example:
 *      Math: Prek-8 (main menu) --> Prek-8 Curation Page, 1st grade, 2nd grade, 3rd grade, ... (submenu)
 *
 * Note: For consistent terminology, the main menu is referred to as the "main menu"
 * and the submenu is referred to as the "submenu".
 *
 * The main menu items are curation pages.
 * Curation pages can have children of type DomainLink or DomainObject. In other
 * words, a curation page can be seen as either a domain (math, science, etc.
 * which have DomainObjects for children) or subdomain (Math: Prek-8,
 * Math: High School, etc. which have DomainLinks for children). Both are considered
 * curation pages.
 *
 * The submenu contains the curation page link followed by
 * the course links.
 */
import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import type React from "react";

import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {Body, LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import type {RegionOverrideResult} from "@khan/data-region-override";

import type {DomainObject} from "../types.ts";

import {ExploreKhanmigo} from "./explore-khanmigo.tsx";
import {coursesMenuCoreStyles} from "./shared-styles.ts";
import {SingleCurationPageSubMenu} from "./single-curation-page-submenu.tsx";
import {StandardsGuidesLink} from "./standards-guides-link.tsx";
import type {ItemsType} from "./types.ts";
import {
    shouldShowKhanmigoLink,
    shouldShowStandardsGuidesLink,
} from "./utils.ts";

import caretRightIcon from "@phosphor-icons/core/bold/caret-right-bold.svg";

export type DropdownData = {
    menuItems: Array<DomainObject>;
};

type Props = {
    // All data returned from the query that contains the main menu items and respective children
    domains: DomainObject[];
    // Clicking link should close the learn menu under AppShell.
    onClickLink: () => void;
    // The function to handle a full close of the learn menu
    onClose: () => void;
    // The region override result to determine when to show the standards guide link
    region: RegionOverrideResult;
    // Whether to render the standards guide link
    shouldRenderStandardsGuideLink: boolean;
};

export const CoursesMenuDropdown = forwardRef<ItemsType, Props>(
    (
        {
            domains,
            onClickLink,
            onClose,
            region,
            shouldRenderStandardsGuideLink,
        }: Props,
        ref,
    ): React.ReactElement | null => {
        const publishAnalyticsEvent = usePublishAnalyticsEvent();
        const data: DropdownData = {
            menuItems: domains,
        };
        // The currently selected main menu index
        // We start at 0 because when moving focus into the dropdown for the first time, it doesn't update the mainMenuIdx
        const [mainMenuIdx, setMainMenuIdx] = useState(0);
        // The currently selected submenu index
        const [subMenuIdx, setSubMenuIdx] = useState(-1);
        // Refs to keep track of the menu items
        const mainItemRefs = useRef<ItemsType>([]);
        const subItemRefs = useRef<ItemsType>([]);
        useImperativeHandle(ref, () => mainItemRefs.current);
        // State to keep track of whether the submenu is open
        const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
        // Ref to measure the main menu height
        const mainMenuRef = useRef<HTMLDivElement>(null);
        // State to track the main menu height
        const [mainMenuHeight, setMainMenuHeight] = useState<
            number | undefined
        >(undefined);
        // The number of pinned links (if they exist)
        const pinnedLinksLength =
            (shouldShowStandardsGuidesLink(
                region,
                shouldRenderStandardsGuideLink,
            )
                ? 1
                : 0) + (shouldShowKhanmigoLink() ? 1 : 0);

        const handleClose = () => {
            // Reset the state of the entire menu
            setMainMenuIdx(0);
            setSubMenuIdx(-1);
            setIsSubmenuOpen(false);
            // Reset the refs
            mainItemRefs.current = [];
            subItemRefs.current = [];
            // Trigger callback
            onClose();
        };
        // Function to select the main menu item based on index. Also sets the
        // submenu open state.
        const selectMainMenuItem = (index: number) => {
            // Domain index does not include the pinned links
            const domainIndex = index - pinnedLinksLength;
            if (domainIndex >= 0) {
                publishAnalyticsEvent({
                    eventType: "NavBarCourseMenuCategoryClickedTI",
                    eventSchemaVersion: 1,
                    currentPageUrlPath: location.pathname,
                    categoryId: domains[domainIndex].domainId ?? "",
                });
            }

            // "close" the submenu, resetting the refs
            closeSubmenu();

            // Open the submenu
            openSubmenu();

            // Set the main menu index
            setMainMenuIdx(index);
        };

        const closeSubmenu = () => {
            subItemRefs.current = [];
            setSubMenuIdx(-1);
            setIsSubmenuOpen(false);
        };

        const openSubmenu = () => {
            setIsSubmenuOpen(true);
            setSubMenuIdx(0);
        };

        // This handles the focus of the current selection based on various factors:
        useEffect(() => {
            if (isSubmenuOpen) {
                // Focus on the current submenu item
                if (
                    subItemRefs.current[subMenuIdx] !== undefined &&
                    subMenuIdx !== -1
                ) {
                    subItemRefs.current[subMenuIdx].focus();
                }
            } else {
                // Focus on the current main menu item
                if (
                    mainItemRefs.current[mainMenuIdx] !== undefined &&
                    mainMenuIdx !== -1
                ) {
                    mainItemRefs.current[mainMenuIdx].focus();
                }
            }
        }, [isSubmenuOpen, mainMenuIdx, subMenuIdx, subItemRefs, mainItemRefs]);

        // Effect to measure the main menu height
        useEffect(() => {
            const measureMainMenuHeight = () => {
                if (mainMenuRef.current) {
                    const height =
                        mainMenuRef.current.getBoundingClientRect().height;
                    setMainMenuHeight(height);
                }
            };

            measureMainMenuHeight();
            // Re-measure on window resize
            window.addEventListener("resize", measureMainMenuHeight);
            return () =>
                window.removeEventListener("resize", measureMainMenuHeight);
        }, [data.menuItems, pinnedLinksLength]);

        /*
        New logic for focusing the current selection:
        we need refs to keep track of the main menu items and submenu items so we can focus on the current selection
        The refs for the main menu items won't change
        Howewer, the submenu items need to be updated every time it's opened since they don't exist until the main menu item is selected

        Note that when the submenu is open, the main menu should never be focused
        Pressing the following keys will have the following logic:
        ArrowDown:
        - if submenu is open, go to the next item in the submenu
            - if on the last item, go to the first item
        - if submenu is closed, go to the next item in the main menu
            - if on the last item, go to the first item
        ArrowUp:
        - if submenu is open, go to the previous item in the submenu
            - if on the first item, go to the last item
        - if submenu is closed, go to the previous item in the main menu
            - if on the first item, go to the last item
        ArrowRight:
        - if we're on a pinned link, do nothing
        - if submenu is open, do nothing
        - if submenu is closed, open it with the current main menu item
        ArrowLeft:
        - if submenu is open, close it and focus on the main menu item that was selected
        - if submenu is closed, do nothing
        Escape:
        - if submenu is open, close it and focus on the main menu item that was selected
        - if submenu is closed, close the menu entirely and focus on the "Explore" button
        Spacebar:
        - if we're on a pinned link, follow the link on the currently focused pinned link
        - if submenu is open, follow the link on the currently focused submenu item
        - if submenu is closed, open it with the current main menu item
        Tab:
        - Close the menu entirely and focus on the "Explore" button
        */

        const handleKeyPress = (event: React.KeyboardEvent) => {
            switch (event.key) {
                case "ArrowDown":
                    event.preventDefault();
                    if (isSubmenuOpen) {
                        setSubMenuIdx(
                            (subMenuIdx + 1) % subItemRefs.current.length,
                        );
                    } else {
                        setMainMenuIdx(
                            (mainMenuIdx + 1) %
                                (data.menuItems.length + pinnedLinksLength),
                        );
                    }
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    if (isSubmenuOpen) {
                        setSubMenuIdx(
                            (subMenuIdx - 1 + subItemRefs.current.length) %
                                subItemRefs.current.length,
                        );
                    } else {
                        setMainMenuIdx(
                            (mainMenuIdx -
                                1 +
                                (data.menuItems.length + pinnedLinksLength)) %
                                (data.menuItems.length + pinnedLinksLength),
                        );
                    }
                    break;
                case "ArrowRight":
                    // If we're on a pinned link, do nothing
                    if (mainMenuIdx < pinnedLinksLength) {
                        break;
                    }
                    if (isSubmenuOpen) {
                        // Do nothing
                        break;
                    } else {
                        selectMainMenuItem(mainMenuIdx);
                    }
                    break;
                case "ArrowLeft":
                    if (isSubmenuOpen) {
                        closeSubmenu();
                    } else {
                        // Do nothing
                    }
                    break;
                case "Tab":
                    handleClose();
                    break;
                case "Escape":
                    if (isSubmenuOpen) {
                        closeSubmenu();
                    } else {
                        // Close the menu entirely and focus on the "Explore" button
                        handleClose();
                    }
                    break;
                case " ":
                case "Enter":
                    // If we're on a pinned link, follow the link
                    if (mainMenuIdx < pinnedLinksLength) {
                        // Follow the link on the currently focused pinned link
                        if (mainItemRefs.current[mainMenuIdx]) {
                            mainItemRefs.current[mainMenuIdx].click();
                        }
                        break;
                    }
                    if (isSubmenuOpen) {
                        // Follow the link on the currently focused submenu item
                        if (subItemRefs.current[subMenuIdx]) {
                            subItemRefs.current[subMenuIdx].click();
                        }
                    } else {
                        // Open the submenu with the current main menu item
                        selectMainMenuItem(mainMenuIdx);
                    }
                    break;
                default:
                    // Do nothing
                    break;
            }
        };

        return (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <View
                tag="nav"
                style={styles.container}
                aria-label={t`Explore Menu`}
                onKeyUp={handleKeyPress}
                // Prevents the screen from scrolling when operating the dropdown
                onKeyDown={(e) => e.preventDefault()}
                testId="learn-menu"
            >
                <MainMenu
                    mainItemRefs={mainItemRefs}
                    data={data}
                    selectedIndex={mainMenuIdx}
                    region={region}
                    shouldRenderStandardsGuideLink={
                        shouldRenderStandardsGuideLink
                    }
                    selectMainMenuItem={selectMainMenuItem}
                    pinnedLinksLength={pinnedLinksLength}
                    ref={mainMenuRef}
                    isSubmenuOpen={isSubmenuOpen}
                />
                <View style={styles.divider} />
                <SubMenu
                    isSubmenuOpen={isSubmenuOpen}
                    mainItem={data.menuItems[mainMenuIdx - pinnedLinksLength]}
                    subItemRefs={subItemRefs}
                    mainSubMenuIdx={mainMenuIdx - pinnedLinksLength}
                    onClickLink={onClickLink}
                    mainMenuHeight={mainMenuHeight}
                />
            </View>
        );
    },
);

type MainMenuProps = {
    mainItemRefs: React.MutableRefObject<ItemsType>;
    data: DropdownData;
    selectedIndex: number;
    region: RegionOverrideResult;
    shouldRenderStandardsGuideLink: boolean;
    selectMainMenuItem: (index: number) => void;
    pinnedLinksLength: number;
    isSubmenuOpen: boolean;
};

const MainMenu = forwardRef<HTMLDivElement, MainMenuProps>(
    (
        {
            mainItemRefs,
            data,
            selectedIndex,
            region,
            shouldRenderStandardsGuideLink,
            selectMainMenuItem,
            pinnedLinksLength,
            isSubmenuOpen,
        },
        ref,
    ) => {
        // This function renders the main menu item
        // It returns a function since that's what the Clickable component expects,
        // and we have the flexibility to pass in additional parameters if needed
        const renderMainMenuItem = (mainItem: DomainObject) => {
            return ({hovered}) => {
                return (
                    <>
                        <LabelMedium
                            style={[hovered && {textDecoration: "underline"}]}
                            testId={mainItem.slug}
                        >
                            {mainItem.translatedTitle}
                        </LabelMedium>
                        <PhosphorIcon
                            icon={caretRightIcon}
                            size="small"
                            style={styles.caretIcon}
                            testId="caret-icon"
                        />
                    </>
                );
            };
        };

        return (
            <View
                tag="ul"
                style={styles.mainMenu}
                aria-label={t`Main Menu`}
                ref={ref}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role -- ul with role="menubar" is correct for ARIA menubar pattern
                role="menubar"
            >
                {/* Pinned links */}
                {shouldShowStandardsGuidesLink(
                    region,
                    shouldRenderStandardsGuideLink,
                ) && (
                    <View tag="li" role="none">
                        <StandardsGuidesLink
                            posIdx={0}
                            mainItemRefs={mainItemRefs}
                            isSelected={selectedIndex === 0}
                        />
                    </View>
                )}
                {shouldShowKhanmigoLink() && (
                    <View tag="li" role="none">
                        <ExploreKhanmigo
                            posIdx={
                                shouldShowStandardsGuidesLink(
                                    region,
                                    shouldRenderStandardsGuideLink,
                                )
                                    ? 1
                                    : 0
                            }
                            mainItemRefs={mainItemRefs}
                            isSelected={
                                selectedIndex ===
                                (shouldShowStandardsGuidesLink(
                                    region,
                                    shouldRenderStandardsGuideLink,
                                )
                                    ? 1
                                    : 0)
                            }
                        />
                    </View>
                )}

                {/* First level options */}
                {data.menuItems.map((mainItem, i) => {
                    // The true index in the main menu (including pinned links) (i is the mainMenuIdx)
                    const index = i + pinnedLinksLength;
                    return (
                        <View
                            tag="li"
                            key={mainItem.slug + `-${index}`}
                            role="none"
                        >
                            <Clickable
                                role="menuitem"
                                aria-haspopup="true"
                                aria-expanded={
                                    isSubmenuOpen && selectedIndex === index
                                }
                                style={coursesMenuCoreStyles.menuItem}
                                ref={(node: HTMLButtonElement) => {
                                    if (node) {
                                        mainItemRefs.current[index] = node;
                                    }
                                }}
                                onClick={() => {
                                    selectMainMenuItem(index);
                                }}
                                tabIndex={selectedIndex === index ? 0 : -1}
                            >
                                {renderMainMenuItem(mainItem)}
                            </Clickable>
                        </View>
                    );
                })}
            </View>
        );
    },
);

type SubMenuProps = {
    isSubmenuOpen: boolean;
    mainItem: DomainObject;
    subItemRefs: React.MutableRefObject<object>;
    mainSubMenuIdx: number;
    onClickLink: () => void;
    mainMenuHeight?: number;
};

const SubMenu = ({
    isSubmenuOpen,
    mainItem,
    subItemRefs,
    mainSubMenuIdx,
    onClickLink,
    mainMenuHeight,
}: SubMenuProps) => {
    const subMenuStyle = [
        styles.subMenu,
        mainMenuHeight && {height: mainMenuHeight, maxHeight: mainMenuHeight},
    ];

    // If the submenu is not open, we will render the placeholder text
    if (!isSubmenuOpen) {
        return (
            <View style={subMenuStyle}>
                <Body style={styles.subMenuPlaceholderText}>
                    {t`Select a category to view its courses`}
                </Body>
            </View>
        );
    }
    // If the submenu is open, we will render the corresponding submenu items
    return (
        <View
            tag="ul"
            style={subMenuStyle}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role -- ul with role="menu" is correct for ARIA menu pattern
            role="menu"
            aria-label={t`${mainItem.translatedTitle} Submenu`}
        >
            <SingleCurationPageSubMenu
                item={mainItem}
                mainSubMenuIdx={mainSubMenuIdx}
                subItemRefs={subItemRefs}
                onClickLink={onClickLink}
                isOpen={isSubmenuOpen}
            />
        </View>
    );
};

const MIN_WIDTH = 640;
const styles = StyleSheet.create({
    container: {
        backgroundColor: semanticColor.core.background.base.default,
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.25)",
        minWidth: MIN_WIDTH,
        flexDirection: "row",
        marginTop: sizing.size_040,
        borderRadius: sizing.size_040,
        width: "fit-content",
        position: "relative",
    },
    mainMenu: {
        width: MIN_WIDTH / 2, // Fixed width to keep main menu consistent
        maxHeight: "fit-content",
        padding: sizing.size_120,
    },
    subMenu: {
        width: "fit-content",
        minWidth: MIN_WIDTH / 2,
        padding: sizing.size_120,
        // Apply scrollable styles to ALL submenus for consistent behavior
        overflowY: "auto",
        scrollbarWidth: "thin", // For Firefox
    },
    subMenuPlaceholderText: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
    },
    caretIcon: {
        color: semanticColor.core.foreground.neutral.default,
        marginLeft: sizing.size_240,
    },
    divider: {
        position: "absolute",
        top: sizing.size_120,
        bottom: sizing.size_120,
        // Since this is being calculated, we can't use sizing tokens
        // since those are strings. We use spacing tokens instead.
        left: MIN_WIDTH / 2,
        width: "1px",
        backgroundColor: semanticColor.core.border.neutral.subtle,
    },
});
